<template>
    <div class="ecoscore-overview">
        <div class="ecoscore-overview__header">
            <div>
                <h1>{{ $t('title') }}</h1>
                <p class="t-subtle">{{ $t('subtitle') }}</p>
            </div>

            <SegmentButton
                v-model="timeDuration"
                :options="timeDurationOptions"
            />
        </div>

        <div class="ecoscore-overview__content">
            <VSpinner
                v-if="isLoading"
                size="medium"
                :line-fg-color="spinnerColor"
                :speed="1"
            />

            <template v-else>
                <div class="summary-cards">
                    <div
                        v-for="(card, i) in summaryCards"
                        :key="i"
                        class="summary-card"
                    >
                        <div class="summary-card__icon">
                            <component :is="card.icon" />
                        </div>
                        <div>
                            <p class="summary-card__label">{{ card.title }}</p>
                            <p class="summary-card__value">
                                <b>{{ card.value }}</b>
                                <small>{{ card.unit }}</small>
                            </p>
                        </div>
                    </div>
                </div>

                <div class="charts">
                    <div
                        v-for="(chart, i) in chartConfigs"
                        :key="i"
                        class="chart-card"
                    >
                        <p class="form-label">{{ chart.title }}</p>

                        <ApexChart
                            :height="chart.height"
                            :options="chart.options"
                            :series="chart.series"
                        />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import ApexChart from 'vue-apexcharts'
import VSpinner from 'vue-simple-spinner'
import moment from 'moment'

import { httpHelper } from '@/utils'
import AnalyticsGraphIcon from '@/components/icons/AnalyticsGraphIcon'
import FuelCanIcon from '@/components/icons/FuelCanIcon'
import PinIcon from '@/components/icons/PinIcon'
import SegmentButton from '@/components/redesigned/SegmentButton'

const units = {
    consumption: 'L/100km',
    fuelConsumtion: 'L',
    distance: 'km',
    score: '/ 10',
}

export default {
    name: 'EcoScoreOverviewView',
    components: {
        ApexChart,
        SegmentButton,
        VSpinner,
    },
    data() {
        return {
            fleetData: [],
            isLoading: false,
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
            timeDuration: [10],
            timeDurationOptions: [
                { value: 1, label: this.$tc('days', 1) },
                { value: 10, label: this.$tc('days', 10) },
                { value: 30, label: this.$tc('days', 30) },
            ],
        }
    },
    computed: {
        summaryCards() {
            const totalFuel = this.fleetData.reduce(
                (acc, cur) => acc + cur.total_fuel,
                0
            )
            const totalDistance = this.fleetData.reduce(
                (acc, cur) => acc + cur.total_distance,
                0
            )
            return [
                {
                    icon: FuelCanIcon,
                    title: this.$t('totalFuel'),
                    value: totalFuel.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                    }),
                    unit: units.fuelConsumtion,
                },
                {
                    icon: PinIcon,
                    title: this.$t('totalDistance'),
                    value: totalDistance.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                    }),
                    unit: units.distance,
                },
                {
                    icon: FuelCanIcon,
                    title: this.$t('avgConsumption'),
                    value:
                        totalDistance > 0
                            ? (
                                  (totalFuel / totalDistance) *
                                  100
                              ).toLocaleString(undefined, {
                                  minimumFractionDigits: 1,
                                  maximumFractionDigits: 1,
                              })
                            : '–',
                    unit: units.consumption,
                },
                {
                    icon: AnalyticsGraphIcon,
                    title: this.$t('avgScore'),
                    value: (this.fleetData.length
                        ? this.fleetData.reduce(
                              (acc, cur) => acc + cur.average_score,
                              0
                          ) / this.fleetData.length
                        : 0
                    ).toFixed(1),
                    unit: units.score,
                },
            ]
        },
        chartConfigs() {
            const chartHeight = this.fleetData.length > 10 ? 280 : 220
            const categories = this.fleetData.map(({ date }) =>
                moment(date)
                    .format('ll')
                    .replace(/[\s\d]+$/, '')
                    .replace(/,\s*$/, '')
            )
            return [
                {
                    title: this.$t('dailyFuelConsumption'),
                    height: chartHeight,
                    series: [
                        {
                            name: this.$t('dailyFuelConsumption'),
                            data: this.fleetData.map(item => item.total_fuel),
                        },
                    ],
                    options: this.getChartOptions(
                        categories,
                        units.fuelConsumtion,
                        '#2a4074'
                    ),
                },
                {
                    title: this.$t('dailyDistance'),
                    height: chartHeight,
                    series: [
                        {
                            name: this.$t('dailyDistance'),
                            data: this.fleetData.map(
                                item => item.total_distance
                            ),
                        },
                    ],
                    options: this.getChartOptions(
                        categories,
                        units.distance,
                        '#2e73b8'
                    ),
                },
                {
                    title: this.$t('dailyAvgScore'),
                    height: chartHeight,
                    series: [
                        {
                            name: this.$t('dailyAvgScore'),
                            data: this.fleetData.map(
                                item => item.average_score
                            ),
                        },
                    ],
                    options: this.getChartOptions(
                        categories,
                        units.score,
                        '#339966'
                    ),
                },
            ]
        },
    },
    watch: {
        timeDuration: {
            immediate: true,
            handler() {
                this.fetchFleetData()
            },
        },
    },
    methods: {
        async fetchFleetData() {
            try {
                this.isLoading = true
                const { data } = await httpHelper.get(
                    '/ecoscore/fleet-stats/',
                    {
                        params: {
                            date_after: moment()
                                .subtract(this.timeDuration[0], 'days')
                                .format('YYYY-MM-DD'),
                            date_before: moment().format('YYYY-MM-DD'),
                        },
                    }
                )
                this.fleetData = data.sort(
                    (a, b) => new Date(a.date) - new Date(b.date)
                )
            } catch {
                this.fleetData = []
            } finally {
                this.isLoading = false
            }
        },
        getChartOptions(categories, unit, color) {
            return {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false,
                    },
                },
                colors: [color],
                dataLabels: {
                    enabled: false,
                },
                plotOptions: {
                    bar: {
                        columnWidth: categories.length > 15 ? '90%' : '60%',
                    },
                },
                tooltip: {
                    y: {
                        formatter: value => `${value} ${unit}`,
                    },
                },
                xaxis: {
                    categories,
                    labels: {
                        rotate: categories.length > 10 ? -45 : 0,
                        rotateAlways: categories.length > 10,
                        trim: false,
                        style: {
                            fontSize: '10px',
                        },
                    },
                },
                yaxis: [
                    {
                        labels: {
                            formatter: value => value.toFixed(0),
                            style: {
                                fontSize: '10px',
                            },
                        },
                    },
                ],
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "avgConsumption": "Avg Consumption",
        "avgScore": "Avg Score",
        "dailyAvgScore": "Daily Avg Score",
        "dailyDistance": "Daily Distance",
        "dailyFuelConsumption": "Daily Fuel Consumption",
        "days": "{n} Day | {n} Days",
        "subtitle": "Monitor your truck fleet performance",
        "title": "Fleet Dashboard",
        "totalDistance": "Total Distance",
        "totalFuel": "Total Fuel"
    },
    "de": {
        "avgConsumption": "Ø Verbrauch",
        "avgScore": "Durchschn. Bewertung",
        "dailyAvgScore": "Tägliche Durchschn. Bewertung",
        "dailyDistance": "Tägliche Strecke",
        "dailyFuelConsumption": "Täglicher Kraftstoffverbrauch",
        "days": "{n} Tag | {n} Tage",
        "subtitle": "Überwachen Sie die Leistung Ihrer LKW-Flotte",
        "title": "Flotten-Dashboard",
        "totalDistance": "Gesamtstrecke",
        "totalFuel": "Gesamtkraftstoff"
    },
    "fr": {
        "avgConsumption": "Consommation moy.",
        "avgScore": "Score moyen",
        "dailyDistance": "Distance quotidienne",
        "dailyFuelConsumption": "Consommation quotidienne de carburant",
        "days": "{n} Jour | {n} Jours",
        "subtitle": "Surveillez les performances de votre flotte de camions",
        "title": "Tableau de bord de la flotte",
        "totalDistance": "Distance totale",
        "totalFuel": "Carburant total"

    },
    "it": {
        "avgConsumption": "Consumo medio",
        "avgScore": "Punteggio medio",
        "dailyAvgScore": "Punteggio medio giornaliero",
        "dailyDistance": "Distanza giornaliera",
        "dailyFuelConsumption": "Consumo giornaliero di carburante",
        "days": "{n} Giorno | {n} Giorni",
        "subtitle": "Monitora le prestazioni della tua flotta di camion",
        "title": "Dashboard della flotta",
        "totalDistance": "Distanza totale",
        "totalFuel": "Carburante totale"
    }
}
</i18n>

<style lang="scss" scoped>
.ecoscore-overview {
    &__header {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 1.5rem 2rem;
        background-color: $color-gray-lighter-new;
        border-bottom: $style-border;

        h1 {
            font-size: 28px;
            color: $color-primary;
        }

        .segment-button {
            white-space: nowrap;
        }
    }

    &__content {
        padding: 1.5rem 2rem;
        max-width: 72rem;
        margin: 0 auto;
    }
}

.summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.summary-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    border: $style-border;
    border-radius: 0.75rem;

    &__icon {
        padding: 0.75rem;
        background: $color-gray-lighter-new;
        border-radius: 0.5rem;

        svg {
            display: block;
            width: 1.5rem;
            height: 1.5rem;
        }
    }

    &__label {
        font-size: 0.875rem;
        color: $color-gray-darker;
    }

    &__value {
        font-size: 1.875rem;

        small {
            margin-left: 0.25rem;
            font-size: 1rem;
            color: $color-gray-darker;
        }
    }
}

.charts {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;

    @include respond-to('for-tablet-down') {
        grid-template-columns: 1fr;
    }
}

.chart-card {
    padding: 1rem;
    border: $style-border;
    border-radius: 0.75rem;
    overflow-x: auto;
}

@include respond-to('for-mobile-down') {
    .summary-cards {
        grid-template-columns: 1fr;
    }

    .charts {
        grid-template-columns: 1fr;
    }
}
</style>
